import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import { Button } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_EmployeeUtilization_FindCompanyTable_VM_Labor } from 'ab-api/web/employeeUtilization/findCompanyTable/viewModel';
import type { W_EmployeeUtilization_FindCompanyTable_VM } from 'ab-api/web/employeeUtilization/findCompanyTable/viewModel';

import type { SkillViewModel, SkillOptionViewModel } from 'ab-viewModels/skill.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { CDLStatus, CDLStatusLabel } from '@acceligentllc/shared/enums/cdlStatus';
import { DefaultColor } from '@acceligentllc/shared/enums/color';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import * as ColorUtil from 'af-utils/color.util';
import { downloadCSV } from 'af-utils/csv.utils';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import { loadingToast, successToast } from '@acceligentllc/storybook';

import PlainMultiselectDropdown from 'af-fields/PlainMultiselectDropdown';

import Breadcrumbs from 'af-components/Breadcrumbs';
import Dropdown from 'af-components/Controls/Dropdown';
import LoadingIndicator from 'af-components/LoadingIndicator';
import SkillsColorGrid from 'af-components/SkillsColorGrid';
import type { TableRef } from 'af-components/Table';
import Table from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import DollarCell from 'af-components/Table/Cells/DollarCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import type { TableProps } from 'af-components/Table/types';
import SkillsCell from 'af-components/Table6/Cells/SkillsCell';

import * as EmployeeUtilizationActions from 'af-actions/employeeUtilization';
import * as KPIActions from 'af-actions/kpi';
import * as SkillActions from 'af-actions/skill';

import { getTable, toCSVData } from './helper';
import InfoCard from './InfoCard';
import styles from './styles.module.scss';
import WorkingCell from '../../UtilizationShared/WorkingCellNew';

const NO_SKILLS_OPTION: SkillOptionViewModel = {
	id: -1,
	name: 'NO SKILL',
	color: DefaultColor.NO_SKILL,
};

interface CdlOption {
	id: CDLStatus;
	name: CDLStatusLabel;
}

const cdlOptions: CdlOption[] = Object.keys(CDLStatus).map((_cdlStatus: CDLStatus) => ({ id: _cdlStatus, name: CDLStatusLabel[_cdlStatus] }));

type OwnProps = CustomRouteComponentProps;

type Props = OwnProps & ConnectedProps<typeof connector>;

const _getLaborUtilizationTable = (
	data: Nullable<W_EmployeeUtilization_FindCompanyTable_VM>,
	selectedSkillIds: Nullable<number[]>,
	cdlOption: Nullable<CDLStatus>,
	tableRequestModel: TableQuery
) => {
	const sanitizedRequestModel = new TableQuery(tableRequestModel);

	return data ? getTable(
		sanitizedRequestModel,
		selectedSkillIds,
		cdlOption,
		data
	) : {} as TableContent<W_EmployeeUtilization_FindCompanyTable_VM_Labor>;
};

const _renderLaborSkillOptionItem = (option: SkillViewModel) => {
	const { color, name } = option;

	return (
		<span className="utilization-view__option-item">
			<div className={`utilization-view__option-color-preview ${ColorUtil.getColorBackgroundClass(color)}`} />
			<span>{name}</span>
		</span>
	);
};

const _renderCdlSelectedItem = (optionItem: CdlOption) => {
	if (optionItem === null) {
		return <span className="text-grey">CDL</span>;
	}
	return <>{optionItem.name}</>;
};

const _renderCdlMenuItem = (optionItem: CdlOption) => {
	if (optionItem === null) {
		return <></>;
	}
	return <>{optionItem.name}</>;
};

const _renderSelectedLaborPlaceholderSkillOptionItem = (skills: SkillViewModel[]) => {
	return (
		<div className="utilization-view__selected-labor-skills">
			<SkillsColorGrid skills={skills} />
			<span>Skills</span>
		</div>
	);
};

const _renderDollarAmountCell = () => (cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, number>) => {
	return (
		<DollarCell
			className={styles['revenue-cell']}
			isUnitPrice={true}
			maxFractionDigits={2}
			value={cell.getValue()}
		/>
	);
};

const _renderWorkingCell = (cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, {
	daysAssigned: number;
	daysAvailable: number;
	daysUnavailable: number;
	totalDays: number;
}>) => {
	return (
		<WorkingCell
			daysAssigned={cell.row.original.daysAssigned}
			daysAvailable={cell.row.original.daysAvailable}
			daysUnavailable={cell.row.original.daysUnavailable}
			totalDays={cell.row.original.totalDays}
		/>
	);
};

const LaborUtilizationTable: React.FC<Props> = (props) => {
	const {
		companyName,
		findAllForDropdown,
		fetchEmployeeUtilizationForCompanyTable,
		history,
		location: { state: { orgAlias } },
		refreshLaborUtilizationCache,
	} = props;

	const [utilizationData, setUtilizationData] = React.useState<Nullable<W_EmployeeUtilization_FindCompanyTable_VM>>(null);
	const [skills, setSkills] = React.useState<SkillOptionViewModel[]>([]);
	const [selectedSkills, setSelectedSkills] = React.useState<SkillOptionViewModel[]>([]);
	const [selectedCdlOption, setSelectedCdlOption] = React.useState<Nullable<CdlOption>>(null);
	const [loading, setLoading] = React.useState(false);

	const tableRef = React.useRef<TableRef<W_EmployeeUtilization_FindCompanyTable_VM_Labor>>(null);

	const goToEmployeePreview = React.useCallback(async (original: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.PREVIEW(original.id.toString(), orgAlias, companyName));
	}, [companyName, history, orgAlias]);

	const goToEmployeeStatusHistory = React.useCallback(async (original: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.STATUS_HISTORY(original.id.toString(), orgAlias, companyName));
	}, [companyName, history, orgAlias]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, unknown>) => {
		const options = [
			{
				label: 'Preview',
				onClick: () => goToEmployeePreview(_cell.row.original),
			},
			{
				label: 'Status History',
				onClick: () => goToEmployeeStatusHistory(_cell.row.original),
			},
		];

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={options}
				valueKey="label"
			/>
		);
	}, [goToEmployeePreview, goToEmployeeStatusHistory]);

	const columns: TableProps<W_EmployeeUtilization_FindCompanyTable_VM_Labor>['columns'] = React.useMemo(() => {
		const _columns: TableProps<W_EmployeeUtilization_FindCompanyTable_VM_Labor>['columns'] = [
			{
				id: 'name',
				header: 'Name',
				accessor: 'fullName',
				enableSorting: true,
				cell: (cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, string>) => <strong>{cell.row.original.fullName}</strong>,
			},
			{
				id: 'timePeriod',
				header: 'Year',
				accessor: 'timePeriodId',
				enableSorting: true,
				cell: (cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, string>) => cell.row.original.timePeriodId || <EmptyCell />,
			},
			{
				id: 'cdlStatus',
				header: 'CDL',
				accessor: 'cdlStatus',
				enableSorting: false,
				cell: (cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, string>) => CDLStatusLabel[cell.row.original.cdlStatus],
			},
			{
				id: 'skills',
				header: 'Skills',
				accessor: 'skills',
				enableSorting: false,
				cell: (cell: CellContext<W_EmployeeUtilization_FindCompanyTable_VM_Labor, string>) => <SkillsCell skills={cell.row.original.skills} />,
			},
			{
				id: 'daysAssigned',
				header: 'Working',
				accessor: 'daysAssigned',
				enableSorting: true,
				cell: _renderWorkingCell,
			},
			{
				id: 'dailyRevenue',
				header: 'Daily Revenue',
				accessor: 'dailyRevenue',
				enableSorting: false,
				cell: _renderDollarAmountCell(),
			},
			{
				id: 'totalRevenue',
				header: 'Total Revenue',
				accessor: 'totalRevenue',
				enableSorting: false,
				cell: _renderDollarAmountCell(),
			},
			{
				id: 'actions',
				isDisplayColumn: true,
				header: () => <EmptyCell isHeader />,
				cell: resolveActionsButton,
			},
		];

		if (process.env.FTD_DISABLE_PR_DIFF !== 'true') {
			const actionsColumnIndex = _columns.findIndex((_column) => _column.id === 'actions');

			_columns.splice(actionsColumnIndex, 0, {
				id: 'payrollDifferential',
				header: 'P/R Diff',
				accessor: 'payrollDifferential',
				enableSorting: false,
				cell: _renderDollarAmountCell(),
			});
		}

		return _columns;
	}, [resolveActionsButton]);

	// On mount
	React.useEffect(() => {
		const initialize = async () => {
			setLoading(true);
			const [
				_skills,
				_utilizationData,
			] = await Promise.all([
				findAllForDropdown(),
				fetchEmployeeUtilizationForCompanyTable(),
			]);
			setSkills([NO_SKILLS_OPTION, ..._skills]);
			setUtilizationData(_utilizationData);
			setLoading(false);
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchTable = React.useCallback(async (tableRequestModel: TableQuery) => {
		return _getLaborUtilizationTable(
			utilizationData,
			selectedSkills.map((_skillOption) => _skillOption.id),
			selectedCdlOption?.id ?? null,
			tableRequestModel
		) ?? {} as TableContent<W_EmployeeUtilization_FindCompanyTable_VM_Labor>;
	}, [selectedCdlOption?.id, selectedSkills, utilizationData]);

	const onRowClick = React.useCallback((row: Row<W_EmployeeUtilization_FindCompanyTable_VM_Labor>) => {
		if (row.original?.id) {
			goToEmployeePreview(row.original);
		}
	}, [goToEmployeePreview]);

	const onValueChange = React.useCallback((_selectedSkills: SkillOptionViewModel[]) => {
		setSelectedSkills(_selectedSkills);
	}, []);

	const onCdlItemChange = React.useCallback((optionItem: CdlOption) => setSelectedCdlOption(optionItem), []);

	const additionalFilter = React.useCallback(() => {
		return (
			<>
				<Dropdown<CdlOption>
					hasBlankOption={true}
					id="cdl-filter"
					isWhite={true}
					labelKey="name"
					onValueChange={onCdlItemChange}
					options={cdlOptions}
					placeholder="Filter by CDL"
					renderMenuItem={_renderCdlMenuItem}
					renderSelected={_renderCdlSelectedItem}
					valueKey="id"
					withBorder={true}
					withCaret={true}
				/>
				<PlainMultiselectDropdown
					containerClassName="utilization-view__labor-skills-dropdown"
					id="multiselectDropdownLaborUtilizationView"
					isWhite={true}
					labelKey="name"
					onValueChange={onValueChange}
					options={skills}
					placeholder="Filter by Skill"
					renderMenuItem={_renderLaborSkillOptionItem}
					renderSelected={_renderSelectedLaborPlaceholderSkillOptionItem}
					selected={selectedSkills}
					valueKey="id"
					withCaret={true}
				/>
			</>
		);
	}, [onCdlItemChange, onValueChange, selectedSkills, skills]);

	const onDownloadCSVClick = React.useCallback(async () => {
		const toastId = 'emp-utilization';

		loadingToast(
			toastId,
			{ title: 'Exporting labor utilization...', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);

		const tableQuery = tableRef.current?.getTableQuery() ?? new TableQuery();

		const csvTableQuery = new TableQuery({
			...tableQuery,
			page: 0,
			pageSize: utilizationData?.rows?.length ?? 0,
		});

		const table = _getLaborUtilizationTable(
			utilizationData,
			selectedSkills.map((_skillOption) => _skillOption.id),
			selectedCdlOption?.id ?? null,
			csvTableQuery
		);

		if (!table) {
			return;
		}

		const filename = `${companyName}_labor-utilization.csv`;
		downloadCSV(toCSVData(table.rows ?? []), filename);

		successToast(
			toastId,
			{ title: 'Labor utilization export complete', description: filename, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);
	}, [companyName, selectedCdlOption?.id, selectedSkills, utilizationData]);

	const breadcrumbs = React.useMemo(() => [{ label: 'Labor Utilization' }], []);

	const renderTable = React.useCallback(() => {
		if (loading) {
			return (
				<LoadingIndicator color="orange" size="big" />
			);
		}

		return (
			<Table
				additionalFilter={additionalFilter}
				buttons={[{
					type: TableButtonType.EXPORT,
					hasPermission: true,
					onClick: onDownloadCSVClick,
					label: 'Export',
				}]}
				columns={columns}
				fetch={fetchTable}
				hasSearchInput
				onRowClick={onRowClick}
				ref={tableRef}
				searchLabel={'Employee'}
				tableName={TableNameEnum.LABOR_UTILIZATION}
			/>
		);
	}, [additionalFilter, columns, fetchTable, loading, onDownloadCSVClick, onRowClick]);

	React.useEffect(() => {
		tableRef.current?.resetPagination();
	}, [selectedSkills, selectedCdlOption]);

	return (
		<div className="utilization-view form-segment">
			<Breadcrumbs items={breadcrumbs} />
			{process.env.ENVIRONMENT === 'TESTING' && <Button onClick={refreshLaborUtilizationCache}>RefreshCache</Button>}
			<InfoCard
				daysAssigned={utilizationData?.daysAssigned ?? 0}
				daysAvailable={utilizationData?.daysAvailable ?? 0}
				daysUnavailable={utilizationData?.daysUnavailable ?? 0}
				targetProfit={utilizationData?.targetProfit ?? 0}
				totalDays={utilizationData?.totalDays ?? 0}
				totalRevenue={utilizationData?.totalRevenue ?? 0}
			/>
			{renderTable()}
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findAllForDropdown: SkillActions.findAllForDropdown,
		fetchEmployeeUtilizationForCompanyTable: EmployeeUtilizationActions.fetchEmployeeUtilizationForCompanyTable,
		refreshLaborUtilizationCache: KPIActions.refreshLaborUtilizationCache,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(LaborUtilizationTable);
